import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Star, ArrowUpRight, BookMarked, FileText, Library, ShoppingBag } from "lucide-react";
import { api, API } from "@/lib/api";
import BuyBookDialog from "@/components/BuyBookDialog";
import AddToCartButton from "@/components/AddToCartButton";

const HERO_IMG = "https://images.pexels.com/photos/1850021/pexels-photo-1850021.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const BOOK_STACK = "https://images.pexels.com/photos/11911071/pexels-photo-11911071.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Home() {
  const [books, setBooks] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [displayedTitles, setDisplayedTitles] = useState(null);

  useEffect(() => {
    api.get("/books").then((r) => setBooks(r.data.slice(0, 4))).catch(() => {});
    api.get("/assignments").then((r) => setAssignments(r.data.slice(0, 3))).catch(() => {});
    api.get("/books/count").then((r) => setDisplayedTitles(r.data.displayed ?? r.data.total)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO — Bento grid, left aligned */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-24">
        <div className="grid grid-cols-12 gap-6 lg:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="col-span-12 lg:col-span-7 flex flex-col justify-between"
          >
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-neutral-500 mb-6">
                <span className="w-8 h-px bg-[#E05A47]" /> Rohini · Est. 1990s
              </div>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#0F172A]">
                Every book,<br/>
                <span className="italic text-[#E05A47]">every assignment,</span><br/>
                a page from<br/> your neighbourhood.
              </h1>
              <p className="mt-8 text-neutral-600 max-w-xl leading-relaxed text-[15px]">
                Chand Book Depot has been Rohini&apos;s go-to stop for school books, stationery and academic material.
                Browse our catalogue, download the latest student assignments, or drop by our shop on Kanjhawala Road.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/books"
                data-testid="hero-browse-books"
                className="group inline-flex items-center gap-2 bg-[#0F172A] text-white px-6 py-3 rounded-full text-sm hover:bg-[#E05A47] transition-colors"
                style={{ transitionProperty: "background-color" }}
              >
                Browse Books
                <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" style={{ transitionProperty: "transform" }} />
              </Link>
              <Link
                to="/assignments"
                data-testid="hero-view-assignments"
                className="inline-flex items-center gap-2 border border-[#0F172A] text-[#0F172A] px-6 py-3 rounded-full text-sm hover:bg-[#0F172A] hover:text-white transition-colors"
                style={{ transitionProperty: "background-color, color" }}
              >
                View Assignments
              </Link>
              <a
                href="tel:09211813521"
                data-testid="hero-call-cta"
                className="inline-flex items-center gap-2 text-[#4A6741] px-3 py-3 text-sm hover:underline"
              >
                <Phone size={14} /> 092118 13521
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="col-span-12 lg:col-span-5 grid grid-cols-2 gap-4"
          >
            <div className="col-span-2 relative rounded-2xl overflow-hidden h-72 lg:h-80 border border-neutral-200">
              <img src={HERO_IMG} alt="Bookstore" className="w-full h-full object-cover"/>
              <div className="absolute inset-0 paper-grain opacity-40 pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/85 backdrop-blur-md rounded-xl px-4 py-3 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-1.5">
                    <Star size={14} className="fill-[#E05A47] text-[#E05A47]" />
                    <span className="font-medium text-sm">3.8</span>
                    <span className="text-xs text-neutral-500">· 747 Reviews</span>
                  </div>
                  <div className="text-xs text-neutral-600 mt-1">Google Business Rating</div>
                </div>
                <a
                  href="https://www.google.com/search?q=CHAND+BOOK+DEPOT+Rohini"
                  target="_blank" rel="noreferrer"
                  className="text-xs text-[#0F172A] underline underline-offset-4"
                  data-testid="google-reviews-link"
                >
                  See on Google
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-neutral-200 bg-white p-5">
              <div className="flex items-center gap-2 text-[#4A6741] text-xs uppercase tracking-[0.22em]">
                <Clock size={14} /> Hours
              </div>
              <div className="mt-2 font-serif text-2xl leading-snug text-[#0F172A]">Closed</div>
              <div className="text-sm text-neutral-500">Opens 9 am Tue</div>
            </div>
            <div className="rounded-2xl border border-neutral-200 bg-[#0F172A] text-white p-5">
              <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-[0.22em]">
                <MapPin size={14} /> Visit
              </div>
              <div className="mt-2 font-serif text-lg leading-tight">
                B-779, Kanjhawala Rd,<br/>Avantika, Rohini
              </div>
              <div className="text-xs text-white/60 mt-1">Sector-1, Delhi 110085</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Books preview */}
      <section className="bg-white border-y border-neutral-200 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-neutral-500 mb-3 flex items-center gap-2 flex-wrap">
                Our Catalogue
                <span data-testid="home-total-books" className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white px-2.5 py-0.5 rounded-full normal-case tracking-normal">
                  <Library size={11}/> {displayedTitles !== null ? displayedTitles.toLocaleString() : "…"} Titles in Stock
                </span>
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl text-[#0F172A] tracking-tight">Featured Books</h2>
            </div>
            <Link to="/books" data-testid="see-all-books" className="text-sm text-[#E05A47] hover:underline underline-offset-4">See all books →</Link>
          </div>

          {books.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-neutral-300 p-12 text-center">
              <BookMarked size={24} className="mx-auto text-neutral-400" />
              <p className="mt-3 text-neutral-500 text-sm">No books added yet. The admin can add books from the dashboard.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {books.map((b) => (
                <BookCard key={b.id} book={b}/>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Assignments preview */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 md:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-neutral-500 mb-3">Study Room</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#0F172A] tracking-tight leading-[0.95]">
              Latest <span className="italic text-[#4A6741]">student assignments</span>
            </h2>
            <p className="mt-6 text-neutral-600 leading-relaxed max-w-md text-[15px]">
              Ready-to-download PDFs and project material curated by the store. Perfect for students
              catching up before submissions.
            </p>
            <Link
              to="/assignments"
              data-testid="see-all-assignments"
              className="mt-8 inline-flex items-center gap-2 border border-[#0F172A] text-[#0F172A] px-5 py-2.5 rounded-full text-sm hover:bg-[#0F172A] hover:text-white transition-colors"
              style={{ transitionProperty: "background-color, color" }}
            >
              Open Assignments Room
            </Link>
          </div>

          <div className="col-span-12 md:col-span-7 grid grid-cols-1 gap-3">
            {assignments.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-neutral-300 p-10 text-center">
                <FileText size={24} className="mx-auto text-neutral-400" />
                <p className="mt-3 text-neutral-500 text-sm">No assignments uploaded yet.</p>
              </div>
            ) : (
              assignments.map((a) => (
                <a
                  key={a.id}
                  href={`${API}/files/assignment/${a.id}`}
                  target="_blank" rel="noreferrer"
                  data-testid={`preview-assignment-${a.id}`}
                  className="group flex items-center justify-between gap-4 p-5 rounded-2xl border border-neutral-200 bg-white card-hover"
                >
                  <div className="flex items-start gap-4 min-w-0">
                    <div className="w-11 h-11 rounded-lg bg-[#F1EDE7] text-[#4A6741] flex items-center justify-center shrink-0">
                      <FileText size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-serif text-xl text-[#0F172A] truncate">{a.title}</div>
                      <div className="text-xs text-neutral-500 mt-0.5 truncate">
                        {a.subject || "General"} · {a.original_filename}
                      </div>
                    </div>
                  </div>
                  <ArrowUpRight size={18} className="text-neutral-400 group-hover:text-[#E05A47] shrink-0" style={{ transitionProperty: "color" }}/>
                </a>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function BookCard({ book }) {
  const [failed, setFailed] = useState(false);
  const cover = book.cover_path && !failed
    ? `${API}/files/book-cover/${book.id}`
    : BOOK_STACK;
  return (
    <div data-testid={`book-card-${book.id}`} className="group">
      <div className="aspect-[3/4] rounded-xl overflow-hidden border border-neutral-200 bg-neutral-100 card-hover">
        <img
          src={cover}
          alt={book.title}
          onError={() => setFailed(true)}
          className="w-full h-full object-cover"
          loading="lazy"
          style={{ transitionProperty: "transform" }}
        />
      </div>
      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">{book.category}</div>
        <div className="font-serif text-xl text-[#0F172A] mt-1 leading-snug">{book.title}</div>
        <div className="text-sm text-neutral-500">{book.author}</div>
        <div className="mt-2 text-[#E05A47] font-medium">₹ {Number(book.price).toFixed(0)}</div>
        <div className="mt-3 flex gap-2">
          <BuyBookDialog
            book={book}
            size="sm"
            triggerLabel="Buy"
            triggerClass="flex-1"
            testIdPrefix="home-buy"
          />
          <AddToCartButton book={book} size="sm" className="flex-1"/>
        </div>
      </div>
    </div>
  );
}
